import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Drawer,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
} from "antd";
import { toast } from "react-toastify";
import moment from "moment";
import SidebarNav from "../sidebar";
import {
  getWalletTxByIdV2,
  releaseWalletTx,
  rejectWalletTx,
} from "../../../service/api";
import request from "../../../service/request";
import PermissionGate from "../commoncomponent/PermissionGate";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { triggerDownload } from "../../../config/downloadUtils";

const REJECT_REASONS = [
  { value: "TRM_BLOCK", label: "TRM block" },
  { value: "COMPLY_ADVANTAGE_HIT", label: "ComplyAdvantage hit" },
  { value: "SANCTIONS_HIT", label: "Sanctions hit" },
  { value: "FIREBLOCKS_AML_FAIL", label: "Fireblocks AML failed" },
  { value: "NOTABENE_REJECTED", label: "Notabene rejected" },
  { value: "SIGNZEY_DECLINED", label: "Signzey declined" },
  { value: "MANUAL", label: "Manual — see note" },
  { value: "OTHER", label: "Other" },
];

const STATUS_COLOR = {
  ON_HOLD: "orange",
  COMPLETED: "green",
  REJECTED: "red",
  BLOCKED: "volcano",
  FAILED: "red",
  PROCESSING: "blue",
  SCREENING: "geekblue",
};

const riskColor = (score) => {
  if (score == null) return "default";
  if (score < 30) return "green";
  if (score < 70) return "gold";
  return "red";
};

/** Small helper that fetches through the admin axios client. */
const fetchList = (params) =>
  request
    .get(`admin/wallet-tx`, { params })
    .then((r) => r.data)
    .catch((e) => {
      throw e;
    });

const ALL_COLUMNS = [
  {
    key: "createdAt",
    title: "Date",
    defaultVisible: true,
    render: (r) => moment(r?.createdAt).format("DD/MM/YYYY HH:mm"),
  },
  {
    key: "type",
    title: "Type",
    defaultVisible: true,
    render: (r) => (
      <Tag color={r?.type === "CREDIT" ? "green" : "red"}>{r?.type}</Tag>
    ),
  },
  {
    key: "tokenName",
    title: "Coin",
    defaultVisible: true,
    render: (r) => r?.tokenName || "-",
  },
  {
    key: "chain",
    title: "Chain",
    defaultVisible: true,
    render: (r) => r?.chain || "-",
  },
  {
    key: "amount",
    title: "Amount",
    defaultVisible: true,
    render: (r) =>
      r?.amount !== undefined && r?.amount !== null
        ? `${r.amount} ${r?.tokenName || ""}`
        : "-",
  },
  {
    key: "displayStatus",
    title: "Status",
    defaultVisible: true,
    render: (r) => (
      <span>
        <Tag color={STATUS_COLOR[r?.displayStatus] || "default"}>
          {r?.displayStatus}
        </Tag>
        {r?.fireblocksEnv && (
          <Tag>{String(r.fireblocksEnv).toUpperCase()}</Tag>
        )}
      </span>
    ),
  },
  {
    key: "riskScore",
    title: "Risk",
    defaultVisible: true,
    render: (r) => (
      <Tag color={riskColor(r?.riskScore)}>
        {r?.riskScore == null ? "—" : Math.round(r.riskScore)}
      </Tag>
    ),
  },
  {
    key: "trm",
    title: "TRM",
    defaultVisible: false,
    render: (r) => r?.screeningSummary?.trmRiskLevel || "—",
  },
  {
    key: "notabene",
    title: "Notabene",
    defaultVisible: false,
    render: (r) => r?.screeningSummary?.notabeneStatus || "—",
  },
  {
    key: "signzey",
    title: "Signzey",
    defaultVisible: false,
    render: (r) => r?.screeningSummary?.signzeyDecision || "—",
  },
  {
    key: "fbaml",
    title: "FB AML",
    defaultVisible: false,
    render: (r) => r?.screeningSummary?.fireblocksAmlVerdict || "—",
  },
  {
    key: "comply",
    title: "CompAdv",
    defaultVisible: false,
    render: (r) => r?.screeningSummary?.complyAdvantageHitCount ?? "—",
  },
  {
    key: "holdReason",
    title: "Hold reason",
    defaultVisible: true,
    render: (r) =>
      r?.holdReason ? (
        <span title={r.holdReason} style={{ fontSize: 12 }}>
          {r.holdReason}
          {r?.holdSourceTag ? (
            <Tag style={{ marginLeft: 6 }}>{r.holdSourceTag}</Tag>
          ) : null}
        </span>
      ) : (
        "—"
      ),
  },
];

const ProviderCard = ({ title, data, emptyLabel = "Not screened by this provider" }) => {
  const [showRaw, setShowRaw] = useState(false);
  const hasData =
    data && typeof data === "object" && Object.keys(data).length > 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <b>{title}</b>
        {hasData && (
          <Button size="small" type="link" onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? "Hide raw JSON" : "Show raw JSON"}
          </Button>
        )}
      </div>
      {!hasData ? (
        <div style={{ opacity: 0.5, fontSize: 12 }}>{emptyLabel}</div>
      ) : showRaw ? (
        <pre
          style={{
            background: "#fafafa",
            padding: 10,
            maxHeight: 260,
            overflow: "auto",
            fontSize: 11,
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      ) : (
        <Descriptions size="small" column={1} bordered>
          {Object.entries(data)
            .slice(0, 25)
            .map(([k, v]) => (
              <Descriptions.Item label={k} key={k}>
                {typeof v === "object" ? (
                  <span style={{ fontSize: 11 }}>{JSON.stringify(v)}</span>
                ) : (
                  String(v ?? "—")
                )}
              </Descriptions.Item>
            ))}
        </Descriptions>
      )}
    </div>
  );
};

const WalletTxReview = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("ON_HOLD");
  const [search, setSearch] = useState("");
  const {
    pageSize: sizePerPage,
    setPageSize: setSizePerPage,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "wallet-tx-review-list",
    allColumns: ALL_COLUMNS,
    defaultPageSize: 25,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [actionOpen, setActionOpen] = useState(false);
  const [actionType, setActionType] = useState("release");
  const [actionRow, setActionRow] = useState(null);
  const [actionForm, setActionForm] = useState({
    reviewNote: "",
    userVisibleNote: "",
    reason: "MANUAL",
  });
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchList({ status, search, page, sizePerPage });
      if (res?.status) {
        setRows(res.data?.result || []);
        setTotal(res.data?.total || 0);
      } else {
        toast.error(res?.message || "Load failed", { toastId: 'wallet-tx-load-error' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Load failed", { toastId: 'wallet-tx-load-error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status, page, search, sizePerPage]);

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [sizePerPage, status, search]);

  const openDetail = async (row) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDetail(null);
    try {
      const res = await getWalletTxByIdV2(row._id);
      if (res?.status) setDetail(res.data);
      else toast.error(res?.message || "Load failed", { toastId: 'wallet-tx-load-error' });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Load failed", { toastId: 'wallet-tx-load-error' });
    } finally {
      setDrawerLoading(false);
    }
  };

  const openAction = (row, type) => {
    setActionRow(row);
    setActionType(type);
    setActionForm({ reviewNote: "", userVisibleNote: "", reason: "MANUAL" });
    setActionOpen(true);
  };

  const submitAction = async () => {
    if (!actionForm.reviewNote || actionForm.reviewNote.length < 10) {
      toast.error("Review note is required (min 10 chars)", { toastId: 'wallet-tx-review-validation-error' });
      return;
    }
    setActionSubmitting(true);
    try {
      const body = {
        reviewNote: actionForm.reviewNote,
        userVisibleNote: actionForm.userVisibleNote || undefined,
      };
      const res =
        actionType === "release"
          ? await releaseWalletTx(actionRow._id, body)
          : await rejectWalletTx(actionRow._id, {
              ...body,
              reason: actionForm.reason,
            });
      if (res?.status) {
        toast.success(
          actionType === "release" ? "Transaction released" : "Transaction rejected",
          { toastId: 'wallet-tx-review-success' },
        );
        setActionOpen(false);
        load();
      } else {
        toast.error(res?.message || "Action failed", { toastId: 'wallet-tx-review-error' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Action failed", { toastId: 'wallet-tx-review-error' });
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleDownload = async (format) => {
    setDownloadLoading(true);
    try {
      const res = await fetchList({ status, search, page: 1, sizePerPage: 9999 });
      const result = res?.data?.result ?? [];
      if (!result?.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
      const cols = ALL_COLUMNS?.filter((c) => visibleColumnKeys?.includes(c?.key));
      triggerDownload(format, result, cols, "wallet-tx-review");
    } finally {
      setDownloadLoading(false);
    }
  };

  const actionColumn = {
    key: "__action",
    title: "",
    width: 260,
    render: (_, r) => (
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <Button size="small" onClick={() => openDetail(r)}>
          Details
        </Button>
        {r.displayStatus === "ON_HOLD" && (
          <PermissionGate module="wallet-tx-review" action="update">
            <Button
              size="small"
              type="primary"
              onClick={() => openAction(r, "release")}
            >
              Release
            </Button>
            <Button size="small" danger onClick={() => openAction(r, "reject")}>
              Reject
            </Button>
          </PermissionGate>
        )}
      </div>
    ),
  };

  const columns = [...dynamicColumns, actionColumn];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid" style={{ padding: 24 }}>
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <h4 style={{ margin: 0 }}>Wallet Tx Review</h4>
            <Select
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              style={{ width: 180 }}
              options={[
                { value: "ON_HOLD", label: "ON_HOLD (queue)" },
                { value: "REJECTED", label: "REJECTED" },
                { value: "COMPLETED", label: "COMPLETED" },
                { value: "PROCESSING", label: "PROCESSING" },
                { value: "SCREENING", label: "SCREENING" },
                { value: "", label: "All" },
              ]}
            />
            <Input.Search
              style={{ maxWidth: 360 }}
              placeholder="Search txId / address / coin"
              onSearch={(v) => {
                setSearch(v);
                setPage(1);
              }}
              allowClear
            />
            <Button onClick={load}>Refresh</Button>
          </div>

          <TableToolbar
            pageSize={sizePerPage}
            setPageSize={setSizePerPage}
            pageSizeOptions={pageSizeOptions}
            total={total}
            page={page}
            visibleColumnKeys={visibleColumnKeys}
            allColumns={ALL_COLUMNS}
            toggleColumn={toggleColumn}
            resetColumns={resetColumns}
            onDownload={handleDownload}
            downloadLoading={downloadLoading}
          />

          {loading ? (
            <Spin />
          ) : (
            <Table
              rowKey={(r) => r._id}
              columns={columns}
              dataSource={rows}
              size="small"
              pagination={{
                current: page,
                pageSize: sizePerPage,
                total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }}
              locale={{ emptyText: "No transactions in this bucket." }}
            />
          )}
        </div>
      </div>

      <Drawer
        title={
          detail
            ? `Tx ${detail._id} — ${detail.tokenName} / ${detail.chain}`
            : "Loading…"
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={780}
      >
        {drawerLoading && <Spin />}
        {detail && (
          <>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Type">{detail.type}</Descriptions.Item>
              <Descriptions.Item label="Amount">{detail.amount}</Descriptions.Item>
              <Descriptions.Item label="From">{detail.fromAddress}</Descriptions.Item>
              <Descriptions.Item label="To">{detail.toAddress}</Descriptions.Item>
              <Descriptions.Item label="Tx hash">
                {detail.txHash || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Fireblocks tx id">
                {detail.fireblocksTransactionId || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[detail.displayStatus] || "default"}>
                  {detail.displayStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Fireblocks env">
                {detail.fireblocksEnv || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Risk score">
                <Tag color={riskColor(detail.riskScore)}>
                  {detail.riskScore == null ? "—" : Math.round(detail.riskScore)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Hold source">
                {detail.holdSourceTag || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Hold reason" span={2}>
                {detail.holdReason || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="User-visible note" span={2}>
                {detail.userVisibleNote || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Reviewed by">
                {detail.reviewedBy || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Reviewed at">
                {detail.reviewedAt
                  ? moment(detail.reviewedAt).format("DD/MM/YYYY HH:mm")
                  : "—"}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ height: 16 }} />

            {detail.displayStatus === "ON_HOLD" && (
              <Alert
                style={{ marginBottom: 16 }}
                type="warning"
                showIcon
                message="This transaction is on hold pending compliance review."
              />
            )}

            <Collapse
              defaultActiveKey={["trm", "fbaml", "fbtx"]}
              items={[
                {
                  key: "trm",
                  label: "TRM — screening",
                  children: (
                    <ProviderCard title="TRM screening" data={detail.trmScreeningData} />
                  ),
                },
                {
                  key: "trm2",
                  label: "TRM — transfer",
                  children: (
                    <ProviderCard title="TRM transfer" data={detail.trmTransferData} />
                  ),
                },
                {
                  key: "trm3",
                  label: `TRM — alerts (${Array.isArray(detail.trmAlertData) ? detail.trmAlertData.length : 0})`,
                  children: (
                    <ProviderCard
                      title="TRM alerts"
                      data={{ alerts: detail.trmAlertData || [] }}
                    />
                  ),
                },
                {
                  key: "notabene",
                  label: "Notabene — travel rule",
                  children: (
                    <ProviderCard title="Notabene" data={detail.notabeneData} />
                  ),
                },
                {
                  key: "signzey",
                  label: "Signzey",
                  children: (
                    <ProviderCard title="Signzey" data={detail.signzeyData} />
                  ),
                },
                {
                  key: "comply",
                  label: "ComplyAdvantage",
                  children: (
                    <ProviderCard
                      title="ComplyAdvantage"
                      data={detail.complyAdvantageData}
                    />
                  ),
                },
                {
                  key: "fbaml",
                  label: "Fireblocks AML",
                  children: (
                    <ProviderCard
                      title="Fireblocks AML"
                      data={detail.fireblocksAmlData}
                    />
                  ),
                },
                {
                  key: "fbtx",
                  label: "Fireblocks — full transaction",
                  children: (
                    <ProviderCard
                      title="Fireblocks transaction"
                      data={detail.fireblocksTxDetails}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Drawer>

      <Modal
        title={actionType === "release" ? "Release transaction" : "Reject transaction"}
        open={actionOpen}
        onCancel={() => setActionOpen(false)}
        onOk={submitAction}
        confirmLoading={actionSubmitting}
        okText={actionType === "release" ? "Release" : "Reject"}
        okButtonProps={{ danger: actionType === "reject" }}
      >
        {actionType === "reject" && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, opacity: 0.7 }}>Reason</label>
            <Select
              style={{ width: "100%" }}
              value={actionForm.reason}
              onChange={(v) => setActionForm({ ...actionForm, reason: v })}
              options={REJECT_REASONS}
            />
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, opacity: 0.7 }}>
            Review note (internal, min 10 chars) *
          </label>
          <Input.TextArea
            rows={3}
            value={actionForm.reviewNote}
            onChange={(e) =>
              setActionForm({ ...actionForm, reviewNote: e.target.value })
            }
          />
        </div>
        <div>
          <label style={{ fontSize: 12, opacity: 0.7 }}>
            User-visible note (optional — shown to the user in tx history)
          </label>
          <Input.TextArea
            rows={2}
            value={actionForm.userVisibleNote}
            onChange={(e) =>
              setActionForm({ ...actionForm, userVisibleNote: e.target.value })
            }
          />
        </div>
      </Modal>
    </>
  );
};

export default WalletTxReview;
